/**
 * @deprecated Use PictureGallery from 'app/components/commons/Package/Pictures/PictureGallery' instead.
 * This component is using Redux which is being phased out in favor of React Query.
 * Migration guide: Replace Pictures with PictureGallery and update redux calls to hooks.
 */
import { PlusOutlined } from '@ant-design/icons';
import { message } from 'antd';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';

import { EditPictureModal } from 'app/components/commons/EditPictureModal/EditPictureModal';
import type { Picture } from 'app/components/commons/Uploader/Types';
import { SortableUploadList } from 'app/components/commons/Uploader/UploadList/UploadList';
import { GalleryModal } from 'app/components/pages/Galleries/GalleryModal/GalleryModal';
import {
  tryEditPicture,
  tryRemoveHotelPicture,
} from 'app/redux/actions/pictures';

import './Pictures.scss';

const DEFAULT_PICTURES_CATEGORIES = [
  { name: 'Media gallery', type: 'official' },
];
const DEFAULT_MODAL_SUBTITLE =
  'Select photos in your media gallery or upload new ones to illustrate this space';

export const PicturesComponent: FC<Props> = ({
  value,
  onChange,
  sectionTitle,
  hotelId,
  picturesCategories = DEFAULT_PICTURES_CATEGORIES,
  kind = 'official',
  previewable = false,
  removable = false,
  editable = false,
  draggable = false,
  modalSubtitle = DEFAULT_MODAL_SUBTITLE,
  fetchRooms = false,
  editHotelPicture,
  removeHotelPicture,
  maxPictures,
  disabledUpload = false,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [pictureToEdit, setPictureToEdit] = useState<Picture | null>(null);
  const pictures = internalValue ?? [];

  useEffect(() => {
    if (value) {
      setInternalValue(value);
    }
  }, [value]);

  if (!onChange) {
    return null;
  }

  const handleOnChange = (newValue: Picture[]) => {
    setInternalValue(newValue);
    onChange(newValue);
  };

  const addPictures = (newPictures: Picture[]) => {
    const existingIds = new Set(pictures.map((pic) => pic.id));
    const uniquePictures = newPictures.filter(
      (pic) => !existingIds.has(pic.id)
    );

    if (uniquePictures.length < newPictures.length) {
      message.warning(
        newPictures.length > 1
          ? 'One of your images could not be added because it is already in your selection.'
          : 'This image is already in your selection.'
      );
    }

    if (!uniquePictures.length) {
      return;
    }

    if (!maxPictures || pictures.length + uniquePictures.length < maxPictures) {
      handleOnChange([...pictures, ...uniquePictures]);
    } else {
      handleOnChange([
        ...pictures.slice(0, pictures.length - uniquePictures.length),
        ...uniquePictures,
      ]);
    }
  };

  const handleRemovePicture = (index: number) => {
    if (index >= 0) {
      handleOnChange([
        ...pictures.slice(0, index),
        ...pictures.slice(index + 1),
      ]);
    }
  };

  const onRemove = (uid: string) => {
    const index = pictures.findIndex(
      (pic) => uid === `${pic.pictureId}-${pic.id}`
    );

    handleRemovePicture(index);
  };

  const updatePicture = (values: Partial<Picture>) => {
    const newPicture = { ...pictureToEdit, ...values } as Picture;

    editHotelPicture(newPicture);

    const pictureIdx = pictures.findIndex((pic) => pic.id === newPicture.id);

    pictures[pictureIdx] = newPicture;
    handleOnChange([...pictures]);
  };

  return (
    <div className="pictures-content">
      <SortableUploadList
        pictures={pictures || []}
        uploadingItems={{}}
        onChange={handleOnChange}
        remove={removable ? onRemove : undefined}
        edit={editable ? setPictureToEdit : undefined}
        customButton={
          <div onClick={() => setModalIsVisible(true)}>
            <PlusOutlined />
            <span className="text">Add</span>
          </div>
        }
        previewable={previewable}
        draggable={draggable}
        disabled={disabledUpload}
      />
      <GalleryModal
        visible={modalIsVisible}
        title={sectionTitle}
        onOk={(newPictures) => {
          addPictures(newPictures);
          setModalIsVisible(false);
        }}
        onCancel={() => setModalIsVisible(false)}
        hotelId={hotelId}
        okText="Save"
        cancelText="Cancel"
        categories={picturesCategories}
        subTitle={modalSubtitle}
        kind={kind}
        fetchRooms={fetchRooms}
        maxPictures={maxPictures}
      />
      {!!pictureToEdit && (
        <EditPictureModal
          picture={pictureToEdit}
          closeModal={() => setPictureToEdit(null)}
          savePicture={(newPictureValues) => {
            updatePicture(newPictureValues);
            setPictureToEdit(null);
          }}
          deletePicture={() => {
            if (hotelId) {
              removeHotelPicture(hotelId, pictureToEdit.id);
            }

            onRemove(`${pictureToEdit.pictureId}-${pictureToEdit.id}`);
            setPictureToEdit(null);
          }}
          kind={kind}
          removable={removable}
        />
      )}
    </div>
  );
};

type Props = {
  value?: Picture[];
  onChange?: (pictures: Picture[]) => void;
  sectionTitle: string;
  hotelId?: number;
  picturesCategories?: { name: string; type: string }[];
  kind?: string;
  previewable?: boolean;
  removable?: boolean;
  editable?: boolean;
  draggable?: boolean;
  modalSubtitle?: string;
  fetchRooms?: boolean;
  editHotelPicture: (picture: Picture) => void;
  removeHotelPicture: (hotelId: number, id: number) => void;
  maxPictures?: number;
  disabledUpload?: boolean;
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  editHotelPicture: (picture: Picture) => dispatch(tryEditPicture(picture)),
  removeHotelPicture: (hotelId: number, id: number) =>
    dispatch(tryRemoveHotelPicture(hotelId, id)),
});

export const Pictures = connect(
  undefined,
  mapDispatchToProps
)(PicturesComponent);
